import type { ClockSchedule, VoyageReportKind } from '@felix/domain';
import {
  allowedNextKinds,
  charterStatus,
  cpComparison,
  type LoadCondition,
  SIDE_KINDS,
} from '@felix/domain';
import { Anchor, Button, Modal, RingProgress, Skeleton, Tabs } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconChevronRight,
  IconCircleCheckFilled,
  IconClockFilled,
  IconCloudFilled,
  IconCurrentLocationFilled,
  IconFileText,
  IconGasStationFilled,
  IconGaugeFilled,
  IconLock,
  IconPaperclip,
  IconRoute,
  IconSettingsFilled,
  IconSteeringWheelFilled,
  IconUsersGroup,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatHours } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import type { StatusTone } from '../../StatusBadge.js';
import { followLink, useShell, useUnsavedChanges } from '../../shell-context.js';
import { TOKENS } from '../../tokens.js';
import { membersOn, shiftDate } from '../personnel/rest-hours.js';
import { errorCode } from '../personnel/source.js';
import type { CrewRestReport } from '../personnel/types.js';
import { defaultReportOffset, loadVoyageClock, VoyageClockContext } from './clock-hint.js';
import { hasDirtyFields } from './dirty.js';
import { groupSections, revealErrors, sectionList, useSchemaErrors } from './form-errors.js';
import { FuelLedgerContext, fuelLedgerState, useFuelLedgerStart } from './fuel-ledger.js';
import { type MlcSnapshot, mlcSnapshot } from './mlc.js';
import { NoonReferenceCard } from './NoonPanels.js';
import classes from './NoonReportScreen.module.css';
import {
  FuelForm,
  MachineryForm,
  NavigationForm,
  slipNormal,
  VoyageTab,
  WeatherForm,
} from './NoonSections.js';
import { CpCard, NoonSummaries } from './NoonSummaries.js';
import { nextDue, nextReportPath, onHireSuggested } from './next-due.js';
import { type NoonFormValues, noonTabOf, valuesToInput } from './noon-form.js';
import {
  defaultReportTime,
  earlyNoonDraft,
  earlyNoonNotice,
  formatLatitude,
  formatLongitude,
  formatPlannedTime,
  type NoonDerived,
  noonDerived,
  noonDraft,
  previousLeg,
  refreshedReportTime,
} from './noon-report.js';
import { useNumber } from './numbers.js';
import {
  type AlertLine,
  AlertsCard,
  AttachmentsCard,
  LastSubmitted,
  ReportActions,
  ReportTimeCard,
  SideTitle,
} from './RailCards.js';
import { ReopenDialog } from './ReopenDialog.js';
import { NextOperationSection } from './ReportSections.js';
import { useReportAttachments, useReportPrint } from './report-output.js';
import { contextMismatch, loadVoyageContext, UNLINKED, type VoyageContext } from './screen-load.js';
import type { VoyageSource } from './source.js';
import type { CharterPartyItem, Voyage, VoyageReport } from './types.js';
import { VoidDialog } from './VoidDialog.js';
import {
  ContextMismatchNotice,
  NextDueCard,
  shortLt,
  VoyageStartHint,
  VoyageStartNotice,
} from './VoyageContextNotices.js';
import { VoyageHeadCard } from './VoyageHeadCard.js';
import { pendingVoyageStart } from './voyage-start.js';
import { VoidedNote, WithdrawActions } from './WithdrawActions.js';
import { removableIn } from './withdraw.js';

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | ({ kind: 'ready' } & VoyageContext);

const TABS = ['voyage', 'navigation', 'machinery', 'fuel', 'weather', 'attachments'] as const;
type Tab = (typeof TABS)[number];
/** Sekme ikonları dolu (sefer-noon-report.jpeg; G4-09). */
const TAB_ICONS: Record<Tab, typeof IconRoute> = {
  voyage: IconSteeringWheelFilled,
  navigation: IconCurrentLocationFilled,
  machinery: IconSettingsFilled,
  fuel: IconGasStationFilled,
  weather: IconCloudFilled,
  attachments: IconPaperclip,
};

/** Rapor durumu rozeti (sefer-noon-report.jpeg "Draft" yeşil, onay ikonlu). */
const STATUS_TONE: Record<string, StatusTone> = {
  draft: 'success',
  submitted: 'info',
  reopened: 'warning',
  void: 'danger',
  new: 'neutral',
};

const localDay = (iso: string, tzOffsetMin: number) =>
  new Date(Date.parse(iso) + tzOffsetMin * 60_000).toISOString().slice(0, 10);

/**
 * Ekrandaki rapor. Gemide: açık taslak; yoksa bugünün gönderilmiş raporu (kilitli, yeniden
 * açılabilir); o da yoksa yeni taslak (`null`). Ofiste: son gönderilmiş Noon.
 */
function currentReport(
  reports: readonly VoyageReport[],
  editing: boolean,
  today: string,
): VoyageReport | null {
  // İptal edilen Noon (ADR 0077 §3) kendiliğinden açılmaz; bağlantıyla açılır
  const noons = reports
    .filter((r) => r.kind === 'noon' && r.status !== 'void')
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt));
  const submitted = noons.filter((r) => r.status === 'submitted').at(-1) ?? null;
  if (!editing) return submitted;
  const open = noons.filter((r) => r.status !== 'submitted').at(-1);
  if (open) return open;
  return submitted && localDay(submitted.reportedAt, submitted.tzOffsetMin) === today
    ? submitted
    : null;
}

/** Bacak: raporun zamanına kadar başlayan seyirlerin (COSP) sayısı. */
export function legOf(reports: readonly VoyageReport[], at: string): number {
  return reports.filter(
    (r) =>
      r.kind === 'cosp' && r.status === 'submitted' && Date.parse(r.reportedAt) <= Date.parse(at),
  ).length;
}

/** Yük durumu: rapordan önceki son COSP'tan (ADR 0022 §4; CP değeri buna göre seçilir). */
function loadConditionOf(reports: readonly VoyageReport[], at: string): LoadCondition | null {
  const cosp = reports
    .filter((r) => r.kind === 'cosp' && Date.parse(r.reportedAt) <= Date.parse(at))
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
    .at(-1);
  const value = cosp?.data.loadCondition;
  return value === 'laden' || value === 'ballast' ? value : null;
}

/** Yeni taslağın değerleri: önceki Noon'dan pervane ve yardımcı makineler, açılışlar kapanıştan. */
function newValues(
  reports: readonly VoyageReport[],
  now: Date,
  zoneOffset: number,
  voyage: Pick<Voyage, 'toPort' | 'toPortCode'> | null,
): NoonFormValues {
  const lastNoon = reports
    .filter((r) => r.kind === 'noon' && r.status === 'submitted')
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
    .at(-1);
  // Saat farkı önceki rapordan (gemi saat dilimini değiştirmediyse aynı); öğlen o farka göre
  const tzOffsetMin = lastNoon?.tzOffsetMin ?? zoneOffset;
  const reportedAt = defaultReportTime(now, tzOffsetMin);
  const machinery = (lastNoon?.data.machinery ?? {}) as Record<string, unknown>;
  const propeller = (machinery.propeller ?? {}) as Record<string, unknown>;
  // Sonraki liman ve chart area önceki Noon'dan; yoksa seferin varış limanı (ADR 0059 §5)
  const lastNav = (lastNoon?.data.navigation ?? {}) as Record<string, unknown>;
  const navigation = lastNav.nextPort
    ? {
        nextPort: lastNav.nextPort,
        nextPortCode: lastNav.nextPortCode ?? null,
        chartArea: lastNav.chartArea ?? null,
      }
    : {
        nextPort: voyage?.toPort ?? null,
        nextPortCode: voyage?.toPortCode ?? null,
        chartArea: lastNav.chartArea ?? null,
      };
  const auxiliaries = Array.isArray(machinery.auxiliaries)
    ? (machinery.auxiliaries as Record<string, unknown>[]).map((row) => ({
        name: row.name,
        rpm: null,
        loadPct: null,
        runningHours: null,
        hoursSinceLast: null,
        status: row.status ?? null,
        remarks: null,
      }))
    : [];
  return {
    reportedAt,
    tzOffsetMin,
    lat: '',
    lon: '',
    data: {
      navigation,
      machinery: {
        mainEngine: { status: 'running' },
        propeller: {
          type: propeller.type ?? null,
          pitchM: propeller.pitchM ?? null,
          diameterM: propeller.diameterM ?? null,
        },
        auxiliaries,
      },
      fuel: noonDraft(reports, reportedAt).fuel,
      weather: { wind: {}, sea: {}, current: {} },
      // Noon'un tek sonraki işlemi seyrin devamı (ADR 0057 §5)
      nextOperation: 'sea_passage',
    },
  };
}

function reportValues(report: VoyageReport): NoonFormValues {
  const data = report.data;
  const fuel = (data.fuel ?? {}) as Record<string, unknown>;
  return {
    reportedAt: report.reportedAt,
    tzOffsetMin: report.tzOffsetMin,
    lat: formatLatitude(report.lat),
    lon: formatLongitude(report.lon),
    data: {
      ...data,
      navigation: data.navigation ?? {},
      machinery: {
        mainEngine: {},
        propeller: {},
        auxiliaries: [],
        ...((data.machinery ?? {}) as object),
      },
      fuel: { fuels: [], lubes: [], freshWater: null, ...fuel },
      weather: { wind: {}, sea: {}, current: {}, ...((data.weather ?? {}) as object) },
      // Açık taslakta tek seçenek önceden seçili; gönderilmiş rapor olduğu gibi (ADR 0057 §5)
      nextOperation:
        report.status !== 'submitted' && !data.nextOperation
          ? 'sea_passage'
          : (data.nextOperation ?? null),
    },
  };
}

const systemNow = () => new Date();

export interface NoonReportScreenProps {
  source: VoyageSource;
  /** Saat (testlerde sabit); gemi sunucusu yine kendi saatine göre denetler. */
  now?: () => Date;
}

/**
 * Noon Report (müşteri ekranları `sefer-noon-report.jpeg`, `sefer-noon-report-makine.jpeg`;
 * ADR 0022, ADR 0019). Gemide kaptan ve birinci zabit girer, taslak kaydeder ve gönderir;
 * gönderilen rapor kilitlenir, kaptan gerekçeyle yeniden açar. Ofiste salt okunur.
 */
export function NoonReportScreen({ source, now = systemNow }: NoonReportScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  // Bağlantıdaki sefer (`?voyage=`): planlanan seferin ilk raporu (ADR 0047 §1). Bağlantı yoksa
  // gemide son akış raporunun bağlamı (ADR 0157 §3)
  const [linkedVoyage, setLinkedVoyage] = useState(() =>
    new URLSearchParams(shell?.search ?? '').get('voyage'),
  );
  // Bağlam çelişkisi bildirimi kapatıldı mı ("Bu seferde devam et")
  const [stayHere, setStayHere] = useState(false);
  // Noon'dan sefere bağlama sürüyor (ADR 0157 §2, #2278)
  const [linking, setLinking] = useState(false);
  // Dokunulmamış saat kayıtta yeniden hesaplandıysa bildirim (ADR 0157 §6)
  const refreshNote = useRef<string | null>(null);
  // Geminin CP register'ı (salt okunur): Kiraya Giriş önerisi (ADR 0157 §7)
  const [charterParties, setCharterParties] = useState<CharterPartyItem[] | null>(null);
  const editor = source.editor;
  // Daily Reports'tan gelen bağlantı (`?report=<id>`; ADR 0051 §1): o rapor açılır
  const linked = new URLSearchParams(shell?.search ?? '').get('report');
  const [vesselId, setVesselId] = useState<string | null>(
    new URLSearchParams(shell?.search ?? '').get('vessel'),
  );
  const [state, setState] = useState<Load>({ kind: 'loading' });
  // Gemi saati kaydı: LT farkının varsayılanı ve tutarsızlık uyarısı (ADR 0065 §8)
  const [clock, setClock] = useState<ClockSchedule | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('navigation');
  const [rest, setRest] = useState<{
    crew: CrewRestReport[];
    members: ReadonlySet<string> | null;
  } | null>(null);
  const [busy, setBusy] = useState<'save' | 'submit' | 'reopen' | 'remove' | 'void' | null>(null);
  const [removing, setRemoving] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  const [reopening, setReopening] = useState(false);
  const form = useForm<NoonFormValues>({
    defaultValues: { reportedAt: now().toISOString(), tzOffsetMin: 0, lat: '', lon: '', data: {} },
  });
  // Şema hataları: bayatlayan hata kalkar; gönderimde sekme sekme özet ve ilk hatalı alan
  // (ortak geri bildirimi 2026-10-19)
  const schemaErrors = useSchemaErrors(form);
  const mainRef = useRef<HTMLDivElement>(null);
  const [reveal, setReveal] = useState<number>(0);
  useEffect(() => {
    if (reveal === 0) return;
    const frame = requestAnimationFrame(() => revealErrors(mainRef.current, ''));
    return () => cancelAnimationFrame(frame);
  }, [reveal]);

  /** Gemileri, seferi, raporları ve ekrandaki raporu okur; form ona göre doldurulur. */
  const load = useCallback(
    async (keep?: string | null) => {
      setState({ kind: 'loading' });
      try {
        const context = await loadVoyageContext(source, {
          vesselId,
          editing: !!editor,
          voyageId: linkedVoyage,
          // All Reports "View" (ADR 0082): raporun seferi durumundan bağımsız açılır
          reportId: linked,
          // Gemide sefersiz görünüme de açılır (ADR 0157 §3): "Henüz sefer yok" boş ekranı kalkar
          unlinked: !!editor,
        });
        const at = now();
        // Varsayılan saat farkı gemi saati kaydından (yoksa dilimden), yuvarlanmadan (ADR 0065 §8)
        const vesselClock = context.vessel ? await loadVoyageClock(source, context.vessel) : null;
        setClock(vesselClock);
        const offset = vesselClock ? defaultReportOffset(vesselClock, at.getTime()) : 0;
        const wanted = keep ?? linked;
        const shown =
          (wanted ? context.reports.find((r) => r.id === wanted) : null) ??
          currentReport(context.reports, !!editor, localDay(at.toISOString(), offset));
        setReportId(shown?.id ?? null);
        // Öneriler iptal edilmemiş raporlardan (ADR 0077 §3); önceki rapor geminin zaman
        // çizgisinden (ADR 0083 §1)
        const live = context.timeline.filter((r) => r.status !== 'void');
        form.reset(shown ? reportValues(shown) : newValues(live, at, offset, context.voyage));
        setState({ kind: 'ready', ...context });
      } catch (error) {
        setState({ kind: 'error', code: errorCode(error) });
      }
    },
    [vesselId, source, editor, form, now, linked, linkedVoyage],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const vessel = ready?.vessel ?? null;
  useEffect(() => {
    if (!vessel || !editor || !source.charterParties) {
      setCharterParties(null);
      return;
    }
    let live = true;
    source
      .charterParties(vessel.id)
      .then((register) => live && setCharterParties(register.items))
      .catch(() => live && setCharterParties(null));
    return () => {
      live = false;
    };
  }, [source, vessel, editor]);
  const allReports = ready?.reports ?? [];
  // Hesaplar ve Next Operation iptal edilmemiş raporlardan (ADR 0077 §3)
  const reports = useMemo(() => allReports.filter((r) => r.status !== 'void'), [allReports]);
  // Geminin zaman çizgisi (ADR 0083 §1): Next Operation ve "son rapor" sefere bakılmadan
  const timeline = useMemo(
    () => (ready?.timeline ?? []).filter((r) => r.status !== 'void'),
    [ready],
  );
  const report = allReports.find((r) => r.id === reportId) ?? null;
  const voyage = ready?.voyage ?? null;
  // Sefersiz Noon (`?voyage=none`; ADR 0083 §1)
  const unlinkedView = ready?.unlinkedView ?? false;
  const readOnly =
    !editor ||
    report?.status === 'submitted' ||
    report?.status === 'void' ||
    (report?.can?.edit ?? true) === false;
  // Taslaktan vazgeçme ve gerekçeli iptal yalnız geminin son akış raporunda (ADR 0077 §3, 0083 §1)
  const removable = report ? removableIn(timeline, report.id) : false;
  // Kaydedilmemiş değişiklik: kabuk üzerinden gezinme onay ister (G4-13)
  useUnsavedChanges(!readOnly && hasDirtyFields(form.formState.dirtyFields));
  // Ekler ve ortak PDF (ADR 0057 §3, §7)
  const attachments = useReportAttachments(source, report);
  const printer = useReportPrint(source, { report, reports, voyage, vessel });
  const allowed = useMemo(() => allowedNextKinds(timeline), [timeline]);

  const values = useWatch({ control: form.control }) as NoonFormValues;
  const reportedAt = values.reportedAt ?? report?.reportedAt ?? now().toISOString();
  // Yakıt defteri (ADR 0117 §2.4, Ek Y3): açılıştan sonraki raporda yakıt sütunları defterden
  const ledgerStart = useFuelLedgerStart(source, vessel?.id ?? null);
  const fuelLedger = fuelLedgerState(
    ledgerStart,
    reportedAt,
    report?.id ?? null,
    vessel?.id ?? null,
    source.scope,
  );
  const tzOffsetMin = values.tzOffsetMin ?? 0;
  const previous = previousLeg(
    reports.filter((r) => r.id !== reportId),
    reportedAt,
  );
  const derived = useMemo(
    () => noonDerived({ reportedAt, data: values.data ?? {} }, previous?.reportedAt ?? null),
    [reportedAt, values.data, previous],
  );
  const cp = useMemo(() => {
    if (!voyage) return null;
    const daily =
      derived.totalConsumptionMt !== null && derived.steamingHours
        ? (derived.totalConsumptionMt / derived.steamingHours) * 24
        : null;
    return cpComparison(
      {
        avgSpeedKn: derived.averageSpeedKn,
        dailyConsumptionMt: daily,
        loadCondition: loadConditionOf(reports, reportedAt),
      },
      voyage,
    );
  }, [voyage, derived, reports, reportedAt]);

  // MLC kartı: raporun gününde gemide herkesin dinlenmesi (ADR 0022 §6)
  const day = new Date(Date.parse(reportedAt) + tzOffsetMin * 60_000).toISOString().slice(0, 10);
  useEffect(() => {
    if (!vessel) return;
    let live = true;
    setRest(null);
    Promise.all([
      // Rapor günü ve önceki gün: o günlere değen atamalar; 7 günlük değerler sunucudan
      source.restHours(vessel.id, shiftDate(day, -1), day),
      // Kart yalnız rapor gününde görevli olanlarla (ortak notu P1); okunamazsa süzgeç yok.
      // Raporun farkı saat kaydından geri kalırsa gün tarih çizgisinde atlanmış olabilir: çağrı
      // yapılmaz (sunucu 500)
      membersOn(source, vessel.id, day, clock ?? undefined),
    ])
      .then(([result, members]) => live && setRest({ crew: result.crew, members }))
      .catch(() => live && setRest({ crew: [], members: null }));
    return () => {
      live = false;
    };
  }, [source, vessel, day, clock]);
  const mlc = rest ? mlcSnapshot(rest.crew, day, rest.members) : null;
  const wind = (values.data?.weather as { wind?: { beaufort?: unknown } } | undefined)?.wind;
  const beaufort = typeof wind?.beaufort === 'number' ? wind.beaufort : null;

  const fail = (error: unknown) => {
    const code = errorCode(error);
    setMessage({
      tone: 'danger',
      text: t(`voyage.errors.${code}`, { defaultValue: t('voyage.errors.GENERIC') }),
    });
  };

  /** Taslağı kaydeder; formdaki hatalar alanlarda görünür. */
  const save = async (draft: boolean): Promise<VoyageReport | null> => {
    if (!editor || (!voyage && !unlinkedView)) return null;
    schemaErrors.clear();
    // Saat alanına dokunulmamış yeni Noon: varsayılan kayıt anında yeniden hesaplanır (aynı gemi
    // günü; 12:00 geçtiyse 12:00 LT). FUTURE_DATE aynen (ADR 0157 §6, #2272)
    refreshNote.current = null;
    if (!report && !form.formState.dirtyFields.reportedAt) {
      const offset = form.getValues('tzOffsetMin') ?? 0;
      const refreshed = refreshedReportTime(form.getValues('reportedAt'), now(), offset);
      if (refreshed) {
        form.setValue('reportedAt', refreshed);
        refreshNote.current = t('voyage.context.earlyNoon.refreshed', {
          time: shortLt(refreshed, offset),
        });
      }
    }
    const conversion = valuesToInput(form.getValues(), { draft, fuelLedger: fuelLedger.active });
    if (!conversion.ok) {
      schemaErrors.apply(conversion.errors);
      // Sekmeler yalnız açıkken çizilir: özet sekme adlarıyla, ilk hatalı sekme açılır
      const tabs = conversion.errors.map((error) => noonTabOf(error.field));
      setMessage({
        tone: 'danger',
        text: t('voyage.errors.SUMMARY', {
          count: conversion.errors.length,
          sections: sectionList(
            groupSections(tabs.map((key) => t(`voyage.noon.tabs.${key}`))),
            0,
            '',
          ),
        }),
      });
      if (tabs[0]) setTab(tabs[0]);
      setReveal((n) => n + 1);
      return null;
    }
    const { kind: _kind, ...fields } = conversion.input;
    return report
      ? editor.update(report.id, { rowVersion: report.rowVersion, ...fields })
      : editor.create({ voyageId: voyage?.id ?? null, ...conversion.input });
  };

  const onSave = async () => {
    setBusy('save');
    setMessage(null);
    try {
      const saved = await save(true);
      if (saved) {
        await load(saved.id);
        setMessage({
          tone: 'success',
          text: [t('voyage.noon.saved'), refreshNote.current].filter(Boolean).join(' '),
        });
      }
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const onSubmit = async () => {
    if (!editor) return;
    setBusy('submit');
    setMessage(null);
    try {
      // Gönderimde şemanın tamamı istenir (ADR 0022 §3)
      const saved = await save(false);
      if (!saved) return;
      await editor.submit(saved.id, saved.contentHash);
      await load(saved.id);
      setMessage({
        tone: 'success',
        text: [t('voyage.noon.submitted'), refreshNote.current].filter(Boolean).join(' '),
      });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const onRemove = async () => {
    if (!editor || !report) return;
    setBusy('remove');
    try {
      await editor.remove(report.id, report.rowVersion);
      setRemoving(false);
      await load();
      setMessage({ tone: 'success', text: t('voyage.withdraw.removed') });
    } catch (error) {
      setRemoving(false);
      fail(error);
    } finally {
      setBusy(null);
    }
  };

  const onVoid = async (reason: string, password: string | undefined): Promise<string | null> => {
    if (!editor?.voidReport || !report) return null;
    setBusy('void');
    try {
      await editor.voidReport(report.id, {
        contentHash: report.contentHash,
        reason,
        ...(password ? { password } : {}),
      });
      setVoiding(false);
      await load(report.id);
      setMessage({ tone: 'success', text: t('voyage.withdraw.voided') });
      return null;
    } catch (error) {
      return errorCode(error);
    } finally {
      setBusy(null);
    }
  };

  const statusKey = report ? report.status : 'new';
  const lastNoon = reports
    .filter((r) => r.kind === 'noon' && r.submittedAt)
    .sort((a, b) => Date.parse(String(a.submittedAt)) - Date.parse(String(b.submittedAt)))
    .at(-1);
  const header = (
    <PageHeader
      title={t('voyage.noon.title')}
      description={editor ? t('voyage.noon.description') : t('voyage.noon.descriptionOffice')}
      aside={
        vessel ? (
          <VoyageHeadCard
            variant="compact"
            label={
              unlinkedView ? t('voyage.event.unlinkedTitle') : (voyage?.voyageNo ?? vessel.name)
            }
            vessel={vessel}
            vesselOptions={source.scope === 'web' ? ready?.vessels : undefined}
            onSelectVessel={(id) => setVesselId(id)}
            facts={[
              { label: t('voyage.noon.header.voyageNo'), value: voyage?.voyageNo ?? '—' },
              {
                label: t('voyage.noon.header.leg'),
                value: voyage
                  ? String(Math.max(1, legOf(reports, reportedAt))).padStart(2, '0')
                  : '—',
              },
              { label: t('voyage.noon.header.from'), value: voyage?.fromPort ?? '—' },
              { label: t('voyage.noon.header.to'), value: voyage?.toPort ?? '—' },
            ]}
          />
        ) : state.kind === 'loading' ? (
          <Skeleton height={70} width={560} radius="md" />
        ) : null
      }
    />
  );

  if (state.kind === 'error') {
    return (
      <div className={classes.screen}>
        <div className={classes.main}>
          {header}
          <section className={classes.card}>
            <EmptyState
              tone="danger"
              title={t('voyage.noon.error')}
              description={t(`voyage.errors.${state.code}`, {
                defaultValue: t('voyage.errors.GENERIC'),
              })}
              action={
                <Button variant="light" onClick={() => void load()}>
                  {t('common.retry')}
                </Button>
              }
            />
          </section>
        </div>
      </div>
    );
  }

  if (state.kind === 'loading') {
    return (
      <div className={classes.screen} aria-busy="true">
        <div className={classes.main}>
          {header}
          <Skeleton height={56} radius="md" />
          <Skeleton height={380} radius="md" />
          <div className={classes.summaryColumns}>
            <Skeleton height={240} radius="md" />
            <Skeleton height={240} radius="md" />
          </div>
        </div>
        <aside className={classes.side} aria-label={t('voyage.noon.summary.title')}>
          <Skeleton height={84} radius="md" />
          <Skeleton height={260} radius="md" />
          <Skeleton height={200} radius="md" />
        </aside>
      </div>
    );
  }

  const empty = (!voyage && !unlinkedView) || (!editor && !report);
  if (empty) {
    return (
      <div className={classes.screen}>
        <div className={classes.main}>
          {header}
          <section className={classes.card}>
            <EmptyState
              icon={<IconRoute size={22} stroke={1.8} />}
              title={voyage ? t('voyage.noon.noReport') : t('voyage.noon.noVoyageTitle')}
              description={
                voyage
                  ? t('voyage.noon.noReportHint')
                  : source.scope === 'ship'
                    ? t('voyage.noon.noVoyageShip')
                    : t('voyage.noon.noVoyageOffice')
              }
            />
          </section>
        </div>
      </div>
    );
  }

  const print = async (mode: 'print' | 'preview') => {
    const failure = await (mode === 'print' ? printer.print() : printer.preview());
    if (failure) setMessage({ tone: 'danger', text: failure });
  };
  const upload = async (file: File) => {
    const code = await attachments.upload(file);
    setMessage(
      code
        ? {
            tone: 'danger',
            text: t(`voyage.errors.${code}`, { defaultValue: t('voyage.errors.GENERIC') }),
          }
        : { tone: 'success', text: t('voyage.noon.attachments.uploaded', { name: file.name }) },
    );
  };
  const attachmentsCard = (
    <AttachmentsCard
      pdfLabel={t('voyage.noon.attachments.pdf')}
      onPrint={report && printer.ready ? () => void print('print') : undefined}
      printing={printer.printing !== null}
      documents={attachments.documents}
      hrefOf={source.attachmentUrl?.bind(source)}
      onUpload={attachments.canUpload ? (file) => void upload(file) : undefined}
      uploadHint={
        editor && !report
          ? t('voyage.noon.attachments.saveFirst')
          : editor && report?.status === 'submitted'
            ? t('voyage.noon.attachments.locked')
            : null
      }
      uploading={attachments.uploading}
    />
  );
  // "Next Report": geminin gönderilmiş son akış raporu bu Noon ise izin verilen türler (Leg / Event)
  const lastFlow = [...timeline]
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
    .filter((r) => !SIDE_KINDS.has(r.kind))
    .at(-1);
  // Sıradaki rapor, başlatılmamış sefer, bağlam çelişkisi ve erken Noon (ADR 0157 §2, §3, §5, §6)
  const due = nextDue(timeline, now());
  const onHire = editor ? onHireSuggested(timeline, charterParties ?? []) : false;
  const pending = editor && ready ? pendingVoyageStart(ready.timeline, ready.voyages) : null;
  const mismatch = !!editor && !stayHere && linked === null && !!ready && contextMismatch(ready);
  // Yeni Noon'da kayıt anı yeniden hesaplanır; taslağın saati değişmez (şerit buna göre; #2278)
  const early = editor && !readOnly ? earlyNoonNotice(report ?? null, now(), tzOffsetMin) : null;
  const draftFix = editor && report && !readOnly ? earlyNoonDraft(report, now()) : null;
  const nextReports =
    editor && report?.status === 'submitted' && lastFlow?.id === report.id && allowed
      ? {
          kinds: allowed.kinds.filter((kind) => !SIDE_KINDS.has(kind) && kind !== 'noon'),
          open: (kind: string) =>
            shell?.navigate(`/voyage/legs?kind=${kind}&voyage=${voyage?.id ?? UNLINKED}`),
        }
      : null;

  // Sonraki Rapor kartı (ADR 0157 §5): taslak kendi bağlamında, yeni form ekranın bağlamında
  const openNext = editor
    ? (kind: VoyageReportKind) => {
        const path = nextReportPath(kind, due?.draft, voyage?.id ?? null);
        if (kind === 'noon') {
          // Aynı ekran: bağlantıdaki sefer durumdadır, rapor konum sorgusundan
          setStayHere(false);
          setLinkedVoyage(new URLSearchParams(path.split('?')[1]).get('voyage'));
        }
        shell?.navigate(path);
      }
    : null;
  const startVoyage =
    pending && source.planner
      ? () =>
          shell?.navigate(
            `/voyage/new?departure=${pending.departure.id}&back=${encodeURIComponent(
              `/voyage/noon-report?voyage=${UNLINKED}`,
            )}`,
          )
      : null;
  /** Kalkışı ve kuyruğunu sefere bağlar (ADR 0157 §4); sonra o seferde yeniden okunur. */
  const linkPending = (target: Voyage, ids: string[]) => {
    if (!source.planner?.link) return;
    setLinking(true);
    source.planner
      .link(target.id, ids)
      .then(() => (linkedVoyage === target.id ? load() : setLinkedVoyage(target.id)))
      .catch(fail)
      .finally(() => setLinking(false));
  };

  const actions =
    editor && (voyage || unlinkedView) ? (
      <ReportActions
        readOnly={readOnly}
        busy={busy}
        canReopen={!!report?.can?.reopen}
        canSubmit={report?.can ? report.can.submit : true}
        onSave={() => void onSave()}
        onSubmit={() => void onSubmit()}
        onReopen={() => setReopening(true)}
        layout={tab === 'machinery' ? 'bar' : 'stack'}
        message={message}
        {...(report && printer.ready
          ? { onPrint: () => void print('print'), onPreview: () => void print('preview') }
          : {})}
        printing={printer.printing}
        lockedNote={
          report?.status === 'submitted' && !report.can?.reopen
            ? t('voyage.withdraw.reopenMasterOnly')
            : null
        }
        extra={
          <WithdrawActions
            report={report}
            removable={removable}
            busy={busy !== null}
            canVoid={!!editor.voidReport}
            onRemove={() => setRemoving(true)}
            onVoid={() => setVoiding(true)}
          />
        }
      />
    ) : null;

  return (
    <VoyageClockContext.Provider value={clock}>
      <FormProvider {...form}>
        <div className={classes.screen} data-tab={tab}>
          <div className={classes.main} ref={mainRef}>
            {header}
            <Tabs
              value={tab}
              onChange={(value) => value && setTab(value as Tab)}
              classNames={{ list: classes.tabList, tab: classes.tab, tabSection: classes.tabIcon }}
              keepMounted={false}
            >
              <Tabs.List>
                {TABS.map((key, index) => {
                  const Icon = TAB_ICONS[key];
                  return (
                    <Tabs.Tab key={key} value={key} leftSection={<Icon size={18} aria-hidden />}>
                      {`${index + 1}. ${t(`voyage.noon.tabs.${key}`)}`}
                    </Tabs.Tab>
                  );
                })}
              </Tabs.List>
            </Tabs>
            {report?.status === 'submitted' ? (
              <div className={classes.notice} data-tone="info">
                <IconLock size={16} aria-hidden />
                <span>{t('voyage.noon.readOnly')}</span>
              </div>
            ) : null}
            {pending ? (
              <VoyageStartNotice
                pending={pending}
                onStart={startVoyage}
                onLink={
                  source.planner?.link
                    ? (target) =>
                        linkPending(
                          target,
                          pending.queue.map((r) => r.id),
                        )
                    : null
                }
                busy={linking}
                onOpen={() =>
                  shell?.navigate(`/voyage/legs?voyage=${UNLINKED}&report=${pending.departure.id}`)
                }
              />
            ) : null}
            {mismatch && lastFlow ? (
              <ContextMismatchNotice
                last={lastFlow}
                lastVoyageNo={
                  ready?.voyages.find((v) => v.id === ready.continuity)?.voyageNo ?? null
                }
                targetVoyageNo={voyage?.voyageNo ?? null}
                onFollow={() => {
                  setStayHere(false);
                  setLinkedVoyage(ready?.continuity ?? UNLINKED);
                }}
                onStay={() => setStayHere(true)}
              />
            ) : null}
            {early ? (
              <div className={classes.notice} data-tone="info" role="status">
                <IconClockFilled size={16} aria-hidden />
                <span>
                  {early.kind === 'new'
                    ? t('voyage.context.earlyNoon.text', {
                        now: shortLt(now().toISOString(), tzOffsetMin),
                      })
                    : t('voyage.context.earlyNoon.draftText', {
                        time: shortLt(early.reportedAt, tzOffsetMin),
                      })}
                </span>
              </div>
            ) : null}
            {draftFix ? (
              <div className={classes.notice} data-tone="warning" role="status">
                <IconAlertTriangle size={16} aria-hidden />
                <span>
                  {t('voyage.context.earlyNoon.draftEarly')}
                  <span className={classes.noticeActions}>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() =>
                        form.setValue('reportedAt', draftFix, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      {t('voyage.context.earlyNoon.fix')}
                    </Button>
                  </span>
                </span>
              </div>
            ) : null}
            <VoidedNote report={report} />
            <div
              className={classes.panels}
              role="tabpanel"
              aria-label={t(`voyage.noon.tabs.${tab}`)}
            >
              {tab === 'voyage' && voyage ? (
                <VoyageTab voyage={voyage} legStart={voyage.start} />
              ) : null}
              {tab === 'voyage' && !voyage ? (
                <section className={classes.card}>
                  <EmptyState
                    icon={<IconRoute size={22} stroke={1.8} />}
                    title={t('voyage.event.unlinkedTitle')}
                    description={t('voyage.event.unlinkedNote')}
                  />
                </section>
              ) : null}
              {tab === 'navigation' ? (
                <>
                  <NavigationForm readOnly={readOnly} derived={derived} previous={previous} />
                  {/* COSP referansı, önceki rapor, ETA ve liman (ADR 0059 §5) */}
                  <NoonReferenceCard
                    reports={reports}
                    currentId={reportId}
                    averageSpeedKn={derived.averageSpeedKn}
                    readOnly={readOnly}
                  />
                  <NoonSummaries
                    derived={derived}
                    cp={cp}
                    mainEngineFuel={voyage?.mainEngineFuel ?? null}
                  />
                  {/* Next Operation (ADR 0057 §5): Noon'da seyrin devamı */}
                  <section className={classes.card}>
                    <NextOperationSection
                      kind="noon"
                      readOnly={readOnly}
                      next={nextReports}
                      due={nextReports ? due : null}
                    />
                  </section>
                </>
              ) : null}
              {tab === 'machinery' ? <MachineryForm readOnly={readOnly} derived={derived} /> : null}
              {tab === 'fuel' ? (
                <>
                  <FuelLedgerContext.Provider value={fuelLedger}>
                    <FuelForm readOnly={readOnly} derived={derived} />
                  </FuelLedgerContext.Provider>
                  <CpCard cp={cp} status={charterStatus(reports, reportedAt)} />
                </>
              ) : null}
              {tab === 'weather' ? <WeatherForm readOnly={readOnly} /> : null}
              {tab === 'attachments' ? attachmentsCard : null}
            </div>
            {tab === 'machinery' ? actions : null}
          </div>

          <aside className={classes.side} aria-label={t('voyage.noon.summary.title')}>
            <ReportTimeCard
              label={t('voyage.noon.header.reportTime')}
              at={reportedAt}
              status={{
                tone: STATUS_TONE[statusKey] ?? 'neutral',
                text: t(`voyage.noon.status.${statusKey}`),
              }}
            />
            {tab === 'machinery' ? (
              <>
                <MachinerySummary derived={derived} />
                <NavigationStatus />
                {/* Sıradaki rapor: seferde ve sefersiz görünümde aynı kart (ADR 0157 §5) */}
                <NextDueCard
                  due={due}
                  eta={
                    voyage
                      ? formatPlannedTime(voyage.plannedEta, voyage.plannedEtaTzOffsetMin)
                      : null
                  }
                  onOpen={openNext}
                  onHire={onHire}
                  extra={
                    pending ? <VoyageStartHint pending={pending} onStart={startVoyage} /> : null
                  }
                />
              </>
            ) : (
              <>
                <MlcCard
                  snapshot={mlc}
                  loading={rest === null}
                  onDetails={
                    shell
                      ? (event) => followLink(event, '/crew/rest-hours', shell.navigate)
                      : undefined
                  }
                />
                <QuickSummary snapshot={mlc} />
                <NoonAlerts
                  slip={derived.slipPct}
                  speed={derived.averageSpeedKn}
                  cpSpeed={cp?.plannedSpeedKn ?? null}
                  beaufort={beaufort}
                  cpBeaufort={voyage?.cpMaxBeaufort ?? null}
                  mlcOk={mlc ? mlc.status === 'compliant' : true}
                />
                {tab === 'attachments' ? null : attachmentsCard}
                {actions ? (
                  <div className={classes.actionCard}>
                    {actions}
                    <LastSubmitted
                      at={lastNoon?.submittedAt ?? null}
                      by={lastNoon?.submittedByName ?? null}
                    />
                  </div>
                ) : (
                  <LastSubmitted
                    at={lastNoon?.submittedAt ?? null}
                    by={lastNoon?.submittedByName ?? null}
                  />
                )}
              </>
            )}
          </aside>
        </div>
        {report && editor ? (
          <ReopenDialog
            opened={reopening}
            onClose={() => setReopening(false)}
            onConfirm={async (reason, password) => {
              setBusy('reopen');
              try {
                await editor.reopen(report.id, {
                  contentHash: report.contentHash,
                  reason,
                  ...(password ? { password } : {}),
                });
                setReopening(false);
                await load(report.id);
                setMessage({ tone: 'success', text: t('voyage.noon.reopened') });
                return null;
              } catch (error) {
                return errorCode(error);
              } finally {
                setBusy(null);
              }
            }}
          />
        ) : null}
        {report && editor?.voidReport ? (
          <VoidDialog
            opened={voiding}
            subject="report"
            onClose={() => setVoiding(false)}
            onConfirm={onVoid}
          />
        ) : null}
        <Modal
          opened={removing}
          onClose={() => setRemoving(false)}
          title={t('voyage.withdraw.removeTitle')}
          closeButtonProps={{ 'aria-label': t('voyage.event.cancel') }}
        >
          <p className={classes.muted}>{t('voyage.withdraw.removeBody')}</p>
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={() => setRemoving(false)}>
              {t('voyage.event.cancel')}
            </Button>
            <Button color="red" loading={busy === 'remove'} onClick={() => void onRemove()}>
              {t('voyage.withdraw.removeDraft')}
            </Button>
          </div>
        </Modal>
      </FormProvider>
    </VoyageClockContext.Provider>
  );
}

const MLC_TONE: Record<MlcSnapshot['status'], string> = {
  compliant: TOKENS['success-fg'],
  warning: TOKENS['warning-fg'],
  violation: TOKENS['danger-fg'],
  incomplete: TOKENS['text-secondary'],
};

function MlcCard({
  snapshot,
  loading,
  onDetails,
}: {
  snapshot: MlcSnapshot | null;
  loading: boolean;
  onDetails: ((event: React.MouseEvent<HTMLAnchorElement>) => void) | undefined;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.noon.mlc.${key}`);
  const ok = snapshot?.status === 'compliant';
  // Dar rayda (1600 altı) halka küçülür, değerler altına iner. 140 px halkanın iç dairesi ≈ 96 px:
  // durum yazısı halkaya değmez (M6f G7)
  const wide = useMediaQuery('(min-width: 1600px)');
  const tone = snapshot ? MLC_TONE[snapshot.status] : TOKENS['text-secondary'];
  // Halka süs: koyu bölüm ve açık ikinci bölüm, altta ince iz (sefer-noon-report.jpeg; G4-23)
  const light = `color-mix(in srgb, ${tone} 35%, ${TOKENS.surface})`;
  // Yazı tonu koyulaştırılır: beyaz üstünde 4,5:1 (WCAG 1.4.3)
  const ink = `color-mix(in srgb, ${tone} 80%, ${TOKENS['navy-900']})`;
  return (
    <section className={classes.sideCard} aria-label={k('title')}>
      <SideTitle
        icon={<IconCircleCheckFilled size={22} color={TOKENS['success-fg']} />}
        title={k('title')}
      />
      {loading ? (
        <Skeleton height={150} />
      ) : snapshot ? (
        <>
          <div className={classes.mlcBody}>
            <RingProgress
              size={wide ? 150 : 140}
              thickness={10}
              rootColor={`color-mix(in srgb, ${tone} 12%, ${TOKENS.surface})`}
              aria-label={k(snapshot.status)}
              sections={[
                { value: 84, color: tone },
                { value: 16, color: light },
              ]}
              label={
                <strong className={classes.mlcLabel} style={{ color: ink }}>
                  {k(snapshot.status)}
                </strong>
              }
            />
            <dl className={classes.mlcFacts}>
              <div>
                <dt>{k('rest24h')}</dt>
                <dd>
                  <strong style={{ color: ink }}>{formatHours(snapshot.rest24h)}</strong>
                  <span className={classes.mlcLimit}>
                    {`/ ${formatHours(snapshot.limits.rest24h)}`}
                  </span>
                </dd>
              </div>
              <div>
                <dt>{k('rest7d')}</dt>
                <dd>
                  <strong style={{ color: ink }}>{formatHours(snapshot.rest7d)}</strong>
                  <span className={classes.mlcLimit}>
                    {`/ ${formatHours(snapshot.limits.rest7d)}`}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
          <div className={classes.notice} data-tone={ok ? 'success' : 'warning'}>
            {ok ? (
              <IconCircleCheckFilled size={16} aria-hidden />
            ) : (
              <IconAlertTriangle size={16} aria-hidden />
            )}
            <span>
              {ok ? k('allWithin') : k('notAll')}
              <br />
              <Anchor
                href="/crew/rest-hours"
                onClick={onDetails}
                size="xs"
                className={classes.detailsLink}
              >
                <IconChevronRight size={12} aria-hidden />
                {k('viewDetails')}
              </Anchor>
            </span>
          </div>
        </>
      ) : (
        <EmptyState title={k('noRecords')} />
      )}
    </section>
  );
}

function QuickSummary({ snapshot }: { snapshot: MlcSnapshot | null }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.noon.summary.${key}`);
  const rows: [string, number | null][] = [
    [k('work24h'), snapshot?.work24h ?? null],
    [k('rest24h'), snapshot?.rest24h ?? null],
    [k('work7d'), snapshot?.work7d ?? null],
    [k('rest7d'), snapshot?.rest7d ?? null],
  ];
  return (
    <section className={classes.sideCard} aria-label={k('title')}>
      <SideTitle icon={<IconUsersGroup size={22} />} title={k('title')} />
      <dl className={classes.summaryList}>
        {rows.map(([label, value], index) => (
          <div key={label}>
            <dt>
              {index === 3 ? (
                <IconClockFilled size={16} aria-hidden className={classes.rowIcon} />
              ) : (
                <IconFileText size={16} aria-hidden className={classes.rowIcon} />
              )}
              {label}
            </dt>
            <dd>{formatHours(value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function NoonAlerts({
  slip,
  speed,
  cpSpeed,
  beaufort,
  cpBeaufort,
  mlcOk,
}: {
  slip: number | null;
  speed: number | null;
  cpSpeed: number | null;
  beaufort: number | null;
  cpBeaufort: number | null;
  mlcOk: boolean;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.alerts.${key}`);
  const lines: AlertLine[] = [];
  if (slipNormal(slip) === false) {
    lines.push({
      key: 'slip',
      tone: 'warning',
      text: t('voyage.noon.alerts.slip', { value: n(slip, 2) }),
    });
  }
  if (speed !== null && cpSpeed !== null && speed < cpSpeed) {
    lines.push({
      key: 'cpSpeed',
      tone: 'warning',
      text: t('voyage.noon.alerts.cpSpeed', { value: n(speed, 1), limit: n(cpSpeed, 1) }),
    });
  }
  if (beaufort !== null && cpBeaufort !== null && beaufort > cpBeaufort) {
    lines.push({
      key: 'cpWeather',
      tone: 'warning',
      text: t('voyage.noon.alerts.cpWeather', { value: beaufort, limit: cpBeaufort }),
    });
  }
  if (!mlcOk) lines.push({ key: 'mlc', tone: 'warning', text: k('mlc') });
  return <AlertsCard title={k('title')} lines={lines} empty={k('none')} />;
}

/** Makine sekmesinin özeti (sefer-noon-report-makine.jpeg "Quick Summary"; G4-27). */
function MachinerySummary({ derived }: { derived: NoonDerived }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.machinerySummary.${key}`);
  const navigation = (useWatch({ name: 'data.navigation' }) ?? {}) as Record<string, unknown>;
  const engine = (useWatch({ name: 'data.machinery.mainEngine' }) ?? {}) as Record<string, unknown>;
  const num = (value: unknown) => (typeof value === 'number' ? value : null);
  const lubes = derived.lubes.reduce((sum, row) => sum + row.closing, 0);
  const total =
    derived.totalRobMt === null ? null : Math.round((derived.totalRobMt + lubes) * 1000) / 1000;
  const two = { digits: 2, fixed: true } as const;
  const one = { digits: 1, fixed: true } as const;
  const rows: [string, string][] = [
    [k('engineLoad'), n.unit(num(engine.loadPct), '%', 0)],
    ['STW', n.unit(num(navigation.stw), 'kn', two)],
    ['SOG', n.unit(num(navigation.sog), 'kn', two)],
    [k('slip'), n.unit(derived.slipPct, '%', two)],
    [k('consumption'), n.unit(derived.totalConsumptionMt, 'MT', one)],
    [k('rob'), n.unit(total, 'MT', one)],
  ];
  return (
    <section className={classes.sideCard} aria-label={k('title')}>
      <SideTitle icon={<IconGaugeFilled size={22} />} title={k('title')} />
      <dl className={classes.summaryList} data-plain>
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Seyir durumu: konum, rota ve hız, hava, rüzgâr, deniz ve akıntı (formdan; G4-27). */
function NavigationStatus() {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.navigationStatus.${key}`);
  const values = useWatch() as NoonFormValues;
  const data = (values.data ?? {}) as Record<string, Record<string, unknown> | undefined>;
  const navigation = data.navigation ?? {};
  const weather = (data.weather ?? {}) as Record<string, unknown>;
  const wind = (weather.wind ?? {}) as Record<string, unknown>;
  const sea = (weather.sea ?? {}) as Record<string, unknown>;
  const current = (weather.current ?? {}) as Record<string, unknown>;
  const num = (value: unknown) => (typeof value === 'number' ? value : null);
  const deg = (value: unknown) => (num(value) === null ? '—' : `${n(num(value), 0)}°`);
  const relation =
    typeof current.relation === 'string' ? t(`voyage.noon.relation.${current.relation}`) : null;
  const rows: [string, ReactNode][] = [
    [
      k('position'),
      // Dar rayda enlem ve boylam ayrı satıra iner, kendi içinde bölünmez (M6f G7)
      values.lat && values.lon ? (
        <>
          <span className={classes.coord}>{values.lat}</span>{' '}
          <span className={classes.coord}>{values.lon}</span>
        </>
      ) : (
        '—'
      ),
    ],
    [k('courseSpeed'), `${deg(navigation.course)} / ${n.unit(num(navigation.sog), 'kn', 1)}`],
    [
      k('weather'),
      typeof weather.condition === 'string' && weather.condition ? weather.condition : '—',
    ],
    [
      k('wind'),
      num(wind.speedKn) === null
        ? '—'
        : `${n.unit(num(wind.speedKn), 'kn', 0)} · ${deg(wind.direction)}`,
    ],
    [k('seaState'), n(num(sea.state), 0)],
    [
      k('current'),
      num(current.speedKn) === null
        ? '—'
        : [n.unit(num(current.speedKn), 'kn', 1), relation].filter(Boolean).join(' '),
    ],
  ];
  return (
    <section className={classes.sideCard} aria-label={k('title')}>
      <SideTitle icon={<IconCurrentLocationFilled size={22} />} title={k('title')} />
      <dl className={classes.summaryList} data-plain>
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
